import { createContext, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import moment from 'moment'
import { useDashboardState } from '../hooks/useDashboardState'
import type { DashboardState } from '../hooks/useDashboardState'
import type { ChannelId, CompareId } from '../mock/types'

// ---------------------------------------------------------------------------
// Channel → API source mapping
// ChannelId 'both' → '' (no filter), 'online' → 'online', 'in-store' → 'pos'
// ---------------------------------------------------------------------------
const channelToSource = (channel: ChannelId): string => {
  if (channel === 'online') return 'online'
  if (channel === 'in-store') return 'pos'
  return ''
}

// ---------------------------------------------------------------------------
// Filter state exposed via context
// ---------------------------------------------------------------------------
export interface DashboardFilters {
  startDate: string
  endDate: string
  setDateRange: (start: string, end: string) => void
  locationId: string
  setLocationId: (id: string) => void
  channel: ChannelId
  setChannel: (c: ChannelId) => void
  cmp: CompareId
  setCmp: (c: CompareId) => void
  netRevenue: boolean
  setNetRevenue: (v: boolean) => void
}

export type DashboardContextValue = DashboardState & DashboardFilters

interface DashboardProviderProps {
  children: ReactNode
}

const DashboardContext = createContext<DashboardContextValue | null>(null)

export const DashboardProvider = ({ children }: DashboardProviderProps) => {
  // Date range — mutable state so API re-fetches when the user picks new dates
  const [startDate, setStartDate] = useState(() => moment().subtract(14, 'day').format('YYYY-MM-DD'))
  const [endDate, setEndDate] = useState(() => moment().format('YYYY-MM-DD'))

  const setDateRange = (start: string, end: string) => {
    setStartDate(start)
    setEndDate(end)
  }

  // Filter state — lives here so all widget hooks can read via useDashboard()
  const [locationId, setLocationId] = useState<string>('all')
  const [channel, setChannel] = useState<ChannelId>('both')
  const [cmp, setCmp] = useState<CompareId>('last-period')
  const [netRevenue, setNetRevenue] = useState<boolean>(false)

  const source = useMemo(() => channelToSource(channel), [channel])

  const state = useDashboardState({ startDate, endDate, locationId, source, netRevenue })

  const value = useMemo<DashboardContextValue>(
    () => ({
      ...state,
      startDate,
      endDate,
      setDateRange,
      locationId,
      setLocationId,
      channel,
      setChannel,
      cmp,
      setCmp,
      netRevenue,
      setNetRevenue,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, startDate, endDate, locationId, channel, cmp, netRevenue],
  )

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>
}

export const useDashboard = (): DashboardContextValue => {
  const context = useContext(DashboardContext)
  if (!context) {
    throw new Error('useDashboard must be used within DashboardProvider')
  }
  return context
}
